import React, { useEffect, useState, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { toast } from 'sonner';

/**
 * GoogleAuthButton Component
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback when login succeeds with credential token
 * @param {Function} props.onError - Callback when login fails
 * @param {string} props.text - Button text ('signin_with', 'signup_with', 'continue_with')
 */
export const GoogleAuthButton = ({ onSuccess, onError, text = 'signin_with' }) => {
  const platform = Capacitor.getPlatform();
  const isNative = platform === 'ios' || platform === 'android';
  const isAuthenticatingRef = useRef(false);

  const handleSuccess = (credential) => {
    isAuthenticatingRef.current = false;
    if (onSuccess) {
      onSuccess(credential);
    }
  };

  const handleError = (error) => {
    isAuthenticatingRef.current = false;
    console.error('Google Sign-In Error:', error);
    // Don't show error toast if user cancelled (on native)
    if (error && error !== 'User canceled') {
        toast.error('Google authentication failed. Please try again.');
    }
    if (onError) {
      onError();
    }
  };

  // 1. Native Flow (Capacitor)
  const handleNativeGoogleLogin = async () => {
    if (isAuthenticatingRef.current) return;
    isAuthenticatingRef.current = true;
    try {
      const webClientId = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_GOOGLE_CLIENT_ID) || process.env.REACT_APP_GOOGLE_CLIENT_ID;
      await GoogleSignIn.initialize({ clientId: webClientId, serverClientId: webClientId }).catch(() => {});
      const result = await GoogleSignIn.signIn();
      if (result?.idToken) {
        handleSuccess(result.idToken);
      } else {
        handleError('No ID Token returned.');
      }
    } catch (error) {
      if (error?.message !== 'User canceled') {
        handleError(error);
      } else {
        isAuthenticatingRef.current = false;
      }
    }
  };

  // 2. Web Flow (Custom Button with useGoogleLogin)
  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => handleSuccess(tokenResponse.access_token),
    onError: handleError,
  });

  const handleWebLogin = () => {
    if (isAuthenticatingRef.current) return;
    isAuthenticatingRef.current = true;
    login();
  };

  const labelMap = {
    signin_with: 'Sign in with Google',
    signup_with: 'Sign up with Google',
    continue_with: 'Continue with Google',
  };
  const label = labelMap[text] || 'Sign in with Google';

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={isNative ? handleNativeGoogleLogin : handleWebLogin}
        className="flex items-center justify-center gap-3 px-4 w-full bg-white hover:bg-gray-50 transition-colors duration-200 text-[#1f1f1f] text-[14px] font-medium border border-[#dadce0] shadow-sm rounded-xl"
        style={{ height: '40px' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        <span>{label}</span>
      </button>
    </div>
  );
};

export default GoogleAuthButton;
